import React, { useState, useMemo, useRef } from 'react';
import Form from './components/Form';
import Preview from './components/Preview';
import jsPDF from 'jspdf';
import * as pdfjsLib from 'pdfjs-dist';
import {
    Container,
    CssBaseline,
    Typography,
    Paper,
    Grid,
    Button,
    Box,
    Snackbar,
    IconButton,
    Slide,
    Tooltip,
    CircularProgress,
} from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import {
    SaveAlt as SaveAltIcon,
    Close as CloseIcon,
    DarkMode as DarkModeIcon,
    LightMode as LightModeIcon,
    RestartAlt as RestartAltIcon,
    UploadFile as UploadFileIcon,
} from '@mui/icons-material';
import './App.css';

pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

const initialFormData = {
    name: '',
    email: '',
    phone: '',
    address: '',
    summary: '',
    education: [{ institution: '', degree: '', startYear: '', endYear: '' }],
    experience: [{ company: '', role: '', startYear: '', endYear: '' }],
    skills: '',
    photo: null,
    certifications: [{ name: '', issuer: '', year: '' }],
    projects: [{ title: '', description: '', technologies: '', link: '' }],
    languages: '',
    socialLinks: { linkedin: '', github: '', website: '' },
};

function App() {
    const [formData, setFormData] = useState(initialFormData);
    const [darkMode, setDarkMode] = useState(false);
    const [template, setTemplate] = useState('modern');
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('PDF exported successfully!');
    const [importing, setImporting] = useState(false);
    const fileInputRef = useRef(null);

    const theme = useMemo(
        () =>
            createTheme({
                palette: {
                    mode: darkMode ? 'dark' : 'light',
                    primary: {
                        main: '#7c4dff',
                        light: '#b47cff',
                        dark: '#3f1dcb',
                    },
                    secondary: {
                        main: '#448aff',
                    },
                    background: {
                        default: darkMode ? '#1a1a2e' : '#f5f5f5',
                        paper: darkMode ? 'rgba(30, 30, 60, 0.85)' : 'rgba(255, 255, 255, 0.85)',
                    },
                },
                typography: {
                    fontFamily: "'Inter', 'Segoe UI', 'Roboto', sans-serif",
                    h4: {
                        fontWeight: 800,
                    },
                },
                shape: {
                    borderRadius: 12,
                },
                components: {
                    MuiTextField: {
                        defaultProps: {
                            variant: 'outlined',
                            size: 'small',
                        },
                    },
                    MuiButton: {
                        styleOverrides: {
                            root: {
                                borderRadius: 10,
                                textTransform: 'none',
                                fontWeight: 600,
                            },
                        },
                    },
                },
            }),
        [darkMode]
    );

    const handleSnackbarClose = () => {
        setSnackbarOpen(false);
    };

    const clearForm = () => {
        setFormData(initialFormData);
    };

    const importFromPDF = async (event) => {
        const file = event.target.files[0];
        if (!file) return;
        event.target.value = '';

        setImporting(true);
        try {
            const arrayBuffer = await file.arrayBuffer();
            const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;

            // Extract text preserving line breaks using Y-position gaps
            let allItems = [];
            for (let i = 1; i <= pdf.numPages; i++) {
                const page = await pdf.getPage(i);
                const content = await page.getTextContent();
                const viewport = page.getViewport({ scale: 1 });
                content.items.forEach(item => {
                    if (item.str.trim()) {
                        allItems.push({
                            text: item.str,
                            x: item.transform[4],
                            y: viewport.height - item.transform[5],
                            page: i,
                        });
                    }
                });
            }

            // Sort by page, then Y (top to bottom), then X (left to right)
            allItems.sort((a, b) => a.page - b.page || a.y - b.y || a.x - b.x);

            // Group items into lines based on Y-position proximity
            const lineGroups = [];
            let currentLine = [];
            let lastY = -1;
            for (const item of allItems) {
                if (lastY >= 0 && Math.abs(item.y - lastY) > 3) {
                    if (currentLine.length > 0) {
                        currentLine.sort((a, b) => a.x - b.x);
                        lineGroups.push(currentLine.map(i => i.text).join(' ').trim());
                    }
                    currentLine = [];
                }
                currentLine.push(item);
                lastY = item.y;
            }
            if (currentLine.length > 0) {
                currentLine.sort((a, b) => a.x - b.x);
                lineGroups.push(currentLine.map(i => i.text).join(' ').trim());
            }

            const lines = lineGroups.filter(Boolean);
            const text = lines.join('\n');

            // Debug: log extracted lines so we can see what the PDF produces
            console.log('=== PDF Import: Extracted lines ===');
            lines.forEach((l, i) => console.log(`  [${i}] "${l}"`));

            // Extract email
            const emailMatch = text.match(/[\w.+-]+@[\w-]+\.[\w.]+/);
            const email = emailMatch ? emailMatch[0] : '';

            // Extract phone
            const phoneMatch = text.match(/(\+?\d[\d\s\-().]{7,}\d)/);
            const phone = phoneMatch ? phoneMatch[0].trim() : '';

            // Extract LinkedIn, GitHub, website
            const linkedinMatch = text.match(/(?:https?:\/\/)?(?:www\.)?linkedin\.com\/in\/[\w-]+/i);
            const githubMatch = text.match(/(?:https?:\/\/)?(?:www\.)?github\.com\/[\w-]+/i);
            const websiteMatch = text.match(/https?:\/\/(?!.*(?:linkedin|github))[\w./:-]+/i);

            // Extract name — first line that looks like a person's name
            let name = '';
            for (const line of lines.slice(0, 10)) {
                const trimmed = line.trim();
                if (trimmed.length < 2 || trimmed.length > 60) continue;
                if (trimmed.includes('@') || /^\+?\d/.test(trimmed)) continue;
                if (/https?:\/\/|linkedin|github/i.test(trimmed)) continue;
                // Skip section headers
                if (/^(summary|objective|education|experience|skills|projects|certifications|languages|contact|profile|about)/i.test(trimmed)) continue;
                // A name is typically 2-4 words, mostly letters
                if (/^[A-Za-z\s.'-]{2,50}$/.test(trimmed) && trimmed.split(/\s+/).length <= 5) {
                    name = trimmed;
                    break;
                }
            }
            // Fallback: just use the first short line
            if (!name) {
                for (const line of lines.slice(0, 5)) {
                    const trimmed = line.trim();
                    if (trimmed.length >= 2 && trimmed.length <= 50 && !trimmed.includes('@') && !/^\+?\d/.test(trimmed)) {
                        name = trimmed;
                        break;
                    }
                }
            }

            // Section header detection — match headers even with extra chars like icons, colons, dashes
            const sectionHeaderDefs = [
                { keys: ['education', 'academic', 'qualifications'], canonical: 'education' },
                { keys: ['work experience', 'professional experience', 'experience', 'employment', 'work history'], canonical: 'experience' },
                { keys: ['technical skills', 'core competencies', 'competencies', 'skills'], canonical: 'skills' },
                { keys: ['projects', 'personal projects', 'key projects'], canonical: 'projects' },
                { keys: ['certifications', 'certificates', 'credentials', 'certification'], canonical: 'certifications' },
                { keys: ['languages', 'language'], canonical: 'languages' },
                { keys: ['summary', 'objective', 'profile', 'about me', 'personal statement', 'professional summary', 'career objective', 'career summary'], canonical: 'summary' },
                { keys: ['awards', 'honors', 'achievements'], canonical: 'awards' },
                { keys: ['references'], canonical: 'references' },
                { keys: ['contact', 'contact info', 'contact information', 'personal info', 'personal information', 'personal details'], canonical: 'contact' },
                { keys: ['interests', 'hobbies'], canonical: 'interests' },
                { keys: ['publications'], canonical: 'publications' },
                { keys: ['volunteer', 'volunteering'], canonical: 'volunteer' },
            ];

            // Try to detect if a line is a section header
            const detectSection = (line) => {
                const cleaned = line.replace(/[^a-zA-Z\s]/g, '').trim().toLowerCase();
                // Only match if the cleaned line is short (headers are typically 1-4 words)
                if (cleaned.split(/\s+/).length > 5) return null;
                for (const def of sectionHeaderDefs) {
                    for (const key of def.keys) {
                        if (cleaned === key || cleaned === key + 's' || cleaned === key.replace(/s$/, '')) {
                            return def.canonical;
                        }
                    }
                }
                return null;
            };

            // Build sections map: find section header lines and collect content until next header
            const sections = {};
            let currentSection = '__header__';
            sections[currentSection] = [];

            for (const line of lines) {
                const detected = detectSection(line);
                if (detected) {
                    currentSection = detected;
                    if (!sections[currentSection]) sections[currentSection] = [];
                } else {
                    if (!sections[currentSection]) sections[currentSection] = [];
                    sections[currentSection].push(line);
                }
            }

            console.log('=== PDF Import: Detected sections ===');
            Object.keys(sections).forEach(k => console.log(`  [${k}] (${sections[k].length} lines):`, sections[k]));

            // Helper to get section content by canonical name
            const getSection = (canonicalName) => sections[canonicalName] || [];

            // Extract summary
            const summaryLines = getSection('summary');
            const summaryText = summaryLines.join(' ').trim();

            // Extract skills
            const skillLines = getSection('skills');
            const skills = skillLines.join(' ')
                .replace(/[•●■▪▸►‣⁃]/g, ',')
                .split(/[,\n|]+/)
                .map(s => s.trim())
                .filter(s => s.length > 1 && s.length < 50)
                .join(', ');

            // Extract languages
            const langLines = getSection('languages');
            const languages = langLines.join(' ')
                .replace(/[•●■▪▸►‣⁃]/g, ',')
                .split(/[,\n|]+/)
                .map(s => s.trim())
                .filter(s => s.length > 1 && s.length < 40)
                .join(', ');

            // Year range pattern: "2020 - 2023", "2020 – Present", "2020-present", "Jan 2020 - Dec 2023"
            const yearRangeRe = /(?:[A-Za-z]+\s+)?(\d{4})\s*[-–—to]+\s*(?:[A-Za-z]+\s+)?(\d{4}|present|current|now|ongoing)/i;

            // Extract education entries
            const eduSectionLines = getSection('education');
            const eduEntries = [];
            for (let i = 0; i < eduSectionLines.length; i++) {
                const line = eduSectionLines[i];
                const yrMatch = yearRangeRe.exec(line);
                if (yrMatch) {
                    const lineWithoutYear = line.replace(yrMatch[0], '').replace(/[,|]+$/, '').trim();
                    const prevLine = eduSectionLines[i - 1]?.trim() || '';
                    const nextLine = eduSectionLines[i + 1]?.trim() || '';

                    let institution = '';
                    let degree = '';

                    if (prevLine && !yearRangeRe.test(prevLine)) {
                        institution = prevLine.replace(/[•●■▪▸►‣⁃]/g, '').trim();
                        degree = lineWithoutYear || nextLine;
                    } else {
                        const parts = lineWithoutYear.split(/[,|]/).map(p => p.trim()).filter(Boolean);
                        institution = parts[0] || '';
                        degree = parts.slice(1).join(', ') || nextLine;
                    }

                    degree = degree.replace(/[•●■▪▸►‣⁃]/g, '').trim();
                    if (institution) {
                        eduEntries.push({
                            institution,
                            degree,
                            startYear: yrMatch[1],
                            endYear: yrMatch[2],
                        });
                    }
                }
            }

            // Extract experience entries
            const expSectionLines = getSection('experience');
            const expEntries = [];
            for (let i = 0; i < expSectionLines.length; i++) {
                const line = expSectionLines[i];
                const yrMatch = yearRangeRe.exec(line);
                if (yrMatch) {
                    const lineWithoutYear = line.replace(yrMatch[0], '').replace(/[,|]+$/, '').trim();
                    const prevLine = expSectionLines[i - 1]?.trim() || '';
                    const nextLine = expSectionLines[i + 1]?.trim() || '';

                    let company = '';
                    let role = '';

                    if (prevLine && !yearRangeRe.test(prevLine)) {
                        company = prevLine.replace(/[•●■▪▸►‣⁃-]/g, '').trim();
                        role = lineWithoutYear || nextLine;
                    } else {
                        const parts = lineWithoutYear.split(/[,|]/).map(p => p.trim()).filter(Boolean);
                        company = parts[0] || '';
                        role = parts.slice(1).join(', ') || nextLine;
                    }

                    role = role.replace(/[•●■▪▸►‣⁃-]/g, '').trim();
                    if (company) {
                        expEntries.push({
                            company,
                            role,
                            startYear: yrMatch[1],
                            endYear: yrMatch[2],
                        });
                    }
                }
            }

            // Extract certifications — each bullet/line is a separate certification
            const certLines = getSection('certifications');
            const certEntries = [];
            for (const line of certLines) {
                const trimmed = line.replace(/[•●■▪▸►‣⁃]/g, '').trim();
                if (trimmed.length < 3) continue;
                // Split by common delimiters if multiple certs on one line
                const items = trimmed.split(/[,;]/).map(s => s.trim()).filter(s => s.length > 2);
                for (const item of items) {
                    const yrMatch = /(\d{4})/.exec(item);
                    // Try to separate name and issuer by " - " or " by " or " from "
                    const issuerSplit = item.replace(/\d{4}/, '').split(/\s+[-–—]\s+|\s+by\s+|\s+from\s+/i);
                    certEntries.push({
                        name: issuerSplit[0]?.trim() || item.replace(/\d{4}/, '').trim(),
                        issuer: issuerSplit[1]?.trim() || '',
                        year: yrMatch ? yrMatch[1] : '',
                    });
                }
            }

            // Extract projects
            const projLines = getSection('projects');
            const projEntries = [];
            let currentProj = null;
            for (const line of projLines) {
                const trimmed = line.replace(/[•●■▪▸►‣⁃]/g, '').trim();
                if (!trimmed) continue;

                const urlMatch = trimmed.match(/https?:\/\/[\w./:-]+/);
                const techMatch = trimmed.match(/^(?:tech(?:nolog(?:y|ies))?|stack|built with|tools)[:\s]+(.+)/i);

                if (techMatch && currentProj) {
                    currentProj.technologies = techMatch[1].trim();
                } else if (urlMatch && currentProj) {
                    currentProj.link = urlMatch[0];
                } else if (trimmed.length > 2 && trimmed.length < 80 && !currentProj) {
                    // First line is likely a project title
                    currentProj = { title: trimmed, description: '', technologies: '', link: '' };
                } else if (currentProj && trimmed.length > 2) {
                    // Could be a new project title or a description
                    // Heuristic: short lines without common description words = new project
                    if (trimmed.length < 60 && /^[A-Z]/.test(trimmed) && !trimmed.includes('.') && currentProj.description) {
                        projEntries.push(currentProj);
                        currentProj = { title: trimmed, description: '', technologies: '', link: '' };
                    } else {
                        currentProj.description += (currentProj.description ? ' ' : '') + trimmed;
                    }
                }
            }
            if (currentProj) projEntries.push(currentProj);

            // Extract address from header area (lines before first section)
            const headerLines = sections['__header__'] || [];
            let address = '';
            for (const l of headerLines) {
                const trimmed = l.trim();
                if (trimmed === name) continue;
                if (trimmed.includes('@') || /^\+?\d[\d\s\-().]+$/.test(trimmed)) continue;
                if (/https?:\/\/|linkedin|github/i.test(trimmed)) continue;
                // Address-like: contains comma, or has numbers (street), or location keywords
                if (trimmed.length > 5 && trimmed.length < 100 &&
                    (/[,]/.test(trimmed) || /\d+\s+\w/.test(trimmed) || /\b(?:street|st|ave|road|rd|city|state|zip|country|district|nagar|chowk)\b/i.test(trimmed))) {
                    address = trimmed;
                    break;
                }
            }
            // Fallback: any non-matched header line that's medium length
            if (!address) {
                for (const l of headerLines) {
                    const trimmed = l.trim();
                    if (trimmed === name || trimmed === email || trimmed === phone) continue;
                    if (trimmed.includes('@') || /^\+?\d[\d\s\-().]+$/.test(trimmed)) continue;
                    if (/https?:\/\/|linkedin|github/i.test(trimmed)) continue;
                    if (trimmed.length > 5 && trimmed.length < 80) {
                        address = trimmed;
                        break;
                    }
                }
            }

            setFormData({
                ...initialFormData,
                name: name || '',
                email,
                phone,
                address,
                summary: summaryText.slice(0, 500) || '',
                education: eduEntries.length > 0 ? eduEntries : [{ institution: '', degree: '', startYear: '', endYear: '' }],
                experience: expEntries.length > 0 ? expEntries : [{ company: '', role: '', startYear: '', endYear: '' }],
                skills,
                languages,
                certifications: certEntries.length > 0 ? certEntries : [{ name: '', issuer: '', year: '' }],
                projects: projEntries.length > 0 ? projEntries : [{ title: '', description: '', technologies: '', link: '' }],
                socialLinks: {
                    linkedin: linkedinMatch ? linkedinMatch[0] : '',
                    github: githubMatch ? githubMatch[0] : '',
                    website: websiteMatch ? websiteMatch[0] : '',
                },
            });

            setSnackbarMessage('Resume imported successfully! Review and adjust the fields.');
            setSnackbarOpen(true);
        } catch (err) {
            setSnackbarMessage('Failed to import PDF. Please try a different file.');
            setSnackbarOpen(true);
        } finally {
            setImporting(false);
        }
    };

    const saveToPDF = () => {
        const doc = new jsPDF();
        let y = 20;

        if (template === 'modern') {
            // Modern: sidebar-style layout
            doc.setFillColor(124, 77, 255);
            doc.rect(0, 0, 70, 297, 'F');

            // Sidebar content (white text)
            doc.setTextColor(255, 255, 255);
            doc.setFontSize(10);

            if (formData.photo) {
                try {
                    const imgProps = doc.getImageProperties(formData.photo);
                    const imgW = 40;
                    const imgH = (imgProps.height * imgW) / imgProps.width;
                    doc.addImage(formData.photo, 'JPEG', 15, y, imgW, imgH);
                    y += imgH + 10;
                } catch (e) { /* skip photo on error */ }
            }

            doc.setFontSize(9);
            doc.setFont('helvetica', 'bold');
            doc.text('CONTACT', 10, y);
            y += 8;
            doc.setFont('helvetica', 'normal');
            if (formData.email) { doc.text(formData.email, 10, y); y += 6; }
            if (formData.phone) { doc.text(formData.phone, 10, y); y += 6; }
            if (formData.address) { doc.text(formData.address, 10, y); y += 6; }
            y += 8;

            doc.setFont('helvetica', 'bold');
            doc.text('SKILLS', 10, y);
            y += 8;
            doc.setFont('helvetica', 'normal');
            if (formData.skills) {
                const skillLines = doc.splitTextToSize(formData.skills, 55);
                doc.text(skillLines, 10, y);
                y += skillLines.length * 5 + 8;
            }

            if (formData.languages) {
                doc.setFont('helvetica', 'bold');
                doc.text('LANGUAGES', 10, y);
                y += 8;
                doc.setFont('helvetica', 'normal');
                const langLines = doc.splitTextToSize(formData.languages, 55);
                doc.text(langLines, 10, y);
                y += langLines.length * 5 + 8;
            }

            const socialLinks = [formData.socialLinks?.linkedin, formData.socialLinks?.github, formData.socialLinks?.website].filter(Boolean);
            if (socialLinks.length > 0) {
                doc.setFont('helvetica', 'bold');
                doc.text('LINKS', 10, y);
                y += 8;
                doc.setFont('helvetica', 'normal');
                socialLinks.forEach((link) => {
                    const linkLines = doc.splitTextToSize(link, 55);
                    doc.text(linkLines, 10, y);
                    y += linkLines.length * 5 + 2;
                });
            }

            // Main content (right side)
            doc.setTextColor(50, 50, 50);
            let my = 20;
            doc.setFontSize(22);
            doc.setFont('helvetica', 'bold');
            doc.text(formData.name || 'Your Name', 80, my);
            my += 10;

            if (formData.summary) {
                doc.setFontSize(10);
                doc.setFont('helvetica', 'normal');
                const summaryLines = doc.splitTextToSize(formData.summary, 115);
                doc.text(summaryLines, 80, my);
                my += summaryLines.length * 5 + 8;
            }

            doc.setFontSize(12);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(124, 77, 255);
            doc.text('EDUCATION', 80, my);
            my += 8;
            doc.setTextColor(50, 50, 50);
            doc.setFontSize(10);
            formData.education.forEach((edu) => {
                if (edu.institution) {
                    doc.setFont('helvetica', 'bold');
                    doc.text(edu.institution, 80, my);
                    doc.setFont('helvetica', 'normal');
                    my += 5;
                    doc.text(`${edu.degree}  |  ${edu.startYear} - ${edu.endYear}`, 80, my);
                    my += 8;
                }
            });

            my += 4;
            doc.setFontSize(12);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(124, 77, 255);
            doc.text('EXPERIENCE', 80, my);
            my += 8;
            doc.setTextColor(50, 50, 50);
            doc.setFontSize(10);
            formData.experience.forEach((exp) => {
                if (exp.company) {
                    if (my > 270) { doc.addPage(); my = 15; }
                    doc.setFont('helvetica', 'bold');
                    doc.text(exp.company, 80, my);
                    doc.setFont('helvetica', 'normal');
                    my += 5;
                    doc.text(`${exp.role}  |  ${exp.startYear} - ${exp.endYear}`, 80, my);
                    my += 8;
                }
            });

            if (formData.certifications?.some(c => c.name)) {
                my += 4;
                if (my > 270) { doc.addPage(); my = 15; }
                doc.setFontSize(12);
                doc.setFont('helvetica', 'bold');
                doc.setTextColor(124, 77, 255);
                doc.text('CERTIFICATIONS', 80, my);
                my += 8;
                doc.setTextColor(50, 50, 50);
                doc.setFontSize(10);
                formData.certifications.forEach((cert) => {
                    if (cert.name) {
                        if (my > 270) { doc.addPage(); my = 15; }
                        doc.setFont('helvetica', 'bold');
                        doc.text(cert.name, 80, my);
                        doc.setFont('helvetica', 'normal');
                        my += 5;
                        doc.text(`${cert.issuer}${cert.year ? '  |  ' + cert.year : ''}`, 80, my);
                        my += 8;
                    }
                });
            }

            if (formData.projects?.some(p => p.title)) {
                my += 4;
                if (my > 270) { doc.addPage(); my = 15; }
                doc.setFontSize(12);
                doc.setFont('helvetica', 'bold');
                doc.setTextColor(124, 77, 255);
                doc.text('PROJECTS', 80, my);
                my += 8;
                doc.setTextColor(50, 50, 50);
                doc.setFontSize(10);
                formData.projects.forEach((proj) => {
                    if (proj.title) {
                        if (my > 270) { doc.addPage(); my = 15; }
                        doc.setFont('helvetica', 'bold');
                        doc.text(proj.title, 80, my);
                        doc.setFont('helvetica', 'normal');
                        my += 5;
                        if (proj.technologies) {
                            doc.text(proj.technologies, 80, my);
                            my += 5;
                        }
                        if (proj.description) {
                            const descLines = doc.splitTextToSize(proj.description, 115);
                            doc.text(descLines, 80, my);
                            my += descLines.length * 5 + 3;
                        }
                        my += 4;
                    }
                });
            }

        } else if (template === 'classic') {
            // Classic: single column, formal
            doc.setFontSize(20);
            doc.setFont('helvetica', 'bold');
            doc.text(formData.name || 'Your Name', 105, y, { align: 'center' });
            y += 7;
            doc.setFontSize(10);
            doc.setFont('helvetica', 'normal');
            const contactLine = [formData.email, formData.phone, formData.address].filter(Boolean).join('  |  ');
            doc.text(contactLine, 105, y, { align: 'center' });
            y += 4;
            doc.setDrawColor(124, 77, 255);
            doc.setLineWidth(0.5);
            doc.line(20, y, 190, y);
            y += 8;

            if (formData.photo) {
                try {
                    const imgProps = doc.getImageProperties(formData.photo);
                    const imgW = 35;
                    const imgH = (imgProps.height * imgW) / imgProps.width;
                    doc.addImage(formData.photo, 'JPEG', 87, y, imgW, imgH);
                    y += imgH + 8;
                } catch (e) { /* skip */ }
            }

            if (formData.summary) {
                doc.setFontSize(12);
                doc.setFont('helvetica', 'bold');
                doc.text('SUMMARY', 20, y);
                y += 6;
                doc.setFontSize(10);
                doc.setFont('helvetica', 'normal');
                const lines = doc.splitTextToSize(formData.summary, 170);
                doc.text(lines, 20, y);
                y += lines.length * 5 + 6;
            }

            doc.setFontSize(12);
            doc.setFont('helvetica', 'bold');
            doc.text('EDUCATION', 20, y);
            y += 6;
            doc.setFontSize(10);
            formData.education.forEach((edu) => {
                if (edu.institution) {
                    doc.setFont('helvetica', 'bold');
                    doc.text(edu.institution, 20, y);
                    doc.setFont('helvetica', 'normal');
                    doc.text(`${edu.startYear} - ${edu.endYear}`, 190, y, { align: 'right' });
                    y += 5;
                    doc.text(edu.degree, 20, y);
                    y += 7;
                }
            });

            y += 4;
            doc.setFontSize(12);
            doc.setFont('helvetica', 'bold');
            doc.text('EXPERIENCE', 20, y);
            y += 6;
            doc.setFontSize(10);
            formData.experience.forEach((exp) => {
                if (exp.company) {
                    doc.setFont('helvetica', 'bold');
                    doc.text(exp.company, 20, y);
                    doc.setFont('helvetica', 'normal');
                    doc.text(`${exp.startYear} - ${exp.endYear}`, 190, y, { align: 'right' });
                    y += 5;
                    doc.text(exp.role, 20, y);
                    y += 7;
                }
            });

            if (formData.skills) {
                y += 4;
                doc.setFontSize(12);
                doc.setFont('helvetica', 'bold');
                doc.text('SKILLS', 20, y);
                y += 6;
                doc.setFontSize(10);
                doc.setFont('helvetica', 'normal');
                doc.text(formData.skills, 20, y);
                y += 8;
            }

            if (formData.certifications?.some(c => c.name)) {
                y += 4;
                if (y > 270) { doc.addPage(); y = 15; }
                doc.setFontSize(12);
                doc.setFont('helvetica', 'bold');
                doc.text('CERTIFICATIONS', 20, y);
                y += 6;
                doc.setFontSize(10);
                formData.certifications.forEach((cert) => {
                    if (cert.name) {
                        if (y > 270) { doc.addPage(); y = 15; }
                        doc.setFont('helvetica', 'bold');
                        doc.text(cert.name, 20, y);
                        doc.setFont('helvetica', 'normal');
                        if (cert.year) doc.text(cert.year, 190, y, { align: 'right' });
                        y += 5;
                        doc.text(cert.issuer, 20, y);
                        y += 7;
                    }
                });
            }

            if (formData.projects?.some(p => p.title)) {
                y += 4;
                if (y > 270) { doc.addPage(); y = 15; }
                doc.setFontSize(12);
                doc.setFont('helvetica', 'bold');
                doc.text('PROJECTS', 20, y);
                y += 6;
                doc.setFontSize(10);
                formData.projects.forEach((proj) => {
                    if (proj.title) {
                        if (y > 270) { doc.addPage(); y = 15; }
                        doc.setFont('helvetica', 'bold');
                        doc.text(proj.title, 20, y);
                        doc.setFont('helvetica', 'normal');
                        y += 5;
                        if (proj.technologies) { doc.text(`Tech: ${proj.technologies}`, 20, y); y += 5; }
                        if (proj.description) {
                            const descLines = doc.splitTextToSize(proj.description, 170);
                            doc.text(descLines, 20, y);
                            y += descLines.length * 5 + 3;
                        }
                        y += 4;
                    }
                });
            }

            if (formData.languages) {
                y += 4;
                if (y > 270) { doc.addPage(); y = 15; }
                doc.setFontSize(12);
                doc.setFont('helvetica', 'bold');
                doc.text('LANGUAGES', 20, y);
                y += 6;
                doc.setFontSize(10);
                doc.setFont('helvetica', 'normal');
                doc.text(formData.languages, 20, y);
            }

            const socialLinks = [formData.socialLinks?.linkedin, formData.socialLinks?.github, formData.socialLinks?.website].filter(Boolean);
            if (socialLinks.length > 0) {
                y += 10;
                if (y > 270) { doc.addPage(); y = 15; }
                doc.setFontSize(9);
                doc.setTextColor(120, 120, 120);
                doc.text(socialLinks.join('  |  '), 105, y, { align: 'center' });
                doc.setTextColor(50, 50, 50);
            }

        } else if (template === 'minimal') {
            // Minimal: clean, lots of whitespace
            doc.setFontSize(24);
            doc.setFont('helvetica', 'normal');
            doc.text((formData.name || 'Your Name').toUpperCase(), 20, y);
            y += 8;
            doc.setFontSize(9);
            doc.setTextColor(120, 120, 120);
            const contactLine = [formData.email, formData.phone, formData.address].filter(Boolean).join('   ');
            doc.text(contactLine, 20, y);
            y += 10;
            doc.setDrawColor(200, 200, 200);
            doc.line(20, y, 190, y);
            y += 10;

            doc.setTextColor(50, 50, 50);
            if (formData.summary) {
                doc.setFontSize(10);
                const lines = doc.splitTextToSize(formData.summary, 170);
                doc.text(lines, 20, y);
                y += lines.length * 5 + 10;
            }

            const sectionHeader = (title) => {
                doc.setFontSize(9);
                doc.setTextColor(124, 77, 255);
                doc.setFont('helvetica', 'bold');
                doc.text(title.toUpperCase(), 20, y);
                y += 6;
                doc.setTextColor(50, 50, 50);
                doc.setFont('helvetica', 'normal');
                doc.setFontSize(10);
            };

            sectionHeader('Education');
            formData.education.forEach((edu) => {
                if (edu.institution) {
                    doc.text(`${edu.institution} - ${edu.degree} (${edu.startYear}–${edu.endYear})`, 20, y);
                    y += 7;
                }
            });
            y += 6;

            sectionHeader('Experience');
            formData.experience.forEach((exp) => {
                if (exp.company) {
                    doc.text(`${exp.company} - ${exp.role} (${exp.startYear}–${exp.endYear})`, 20, y);
                    y += 7;
                }
            });
            y += 6;

            if (formData.skills) {
                sectionHeader('Skills');
                doc.text(formData.skills, 20, y);
                y += 8;
            }

            if (formData.certifications?.some(c => c.name)) {
                y += 6;
                if (y > 270) { doc.addPage(); y = 15; }
                sectionHeader('Certifications');
                formData.certifications.forEach((cert) => {
                    if (cert.name) {
                        if (y > 270) { doc.addPage(); y = 15; }
                        doc.text(`${cert.name} - ${cert.issuer}${cert.year ? ' (' + cert.year + ')' : ''}`, 20, y);
                        y += 7;
                    }
                });
            }

            if (formData.projects?.some(p => p.title)) {
                y += 6;
                if (y > 270) { doc.addPage(); y = 15; }
                sectionHeader('Projects');
                formData.projects.forEach((proj) => {
                    if (proj.title) {
                        if (y > 270) { doc.addPage(); y = 15; }
                        doc.setFont('helvetica', 'bold');
                        doc.text(proj.title, 20, y);
                        doc.setFont('helvetica', 'normal');
                        y += 5;
                        if (proj.description) {
                            const descLines = doc.splitTextToSize(proj.description, 170);
                            doc.text(descLines, 20, y);
                            y += descLines.length * 5 + 3;
                        }
                        y += 3;
                    }
                });
            }

            if (formData.languages) {
                y += 6;
                if (y > 270) { doc.addPage(); y = 15; }
                sectionHeader('Languages');
                doc.text(formData.languages, 20, y);
                y += 8;
            }

            const socialLinks = [formData.socialLinks?.linkedin, formData.socialLinks?.github, formData.socialLinks?.website].filter(Boolean);
            if (socialLinks.length > 0) {
                y += 6;
                if (y > 270) { doc.addPage(); y = 15; }
                doc.setFontSize(8);
                doc.setTextColor(120, 120, 120);
                doc.text(socialLinks.join('   '), 20, y);
            }

        } else if (template === 'executive') {
            // Executive: serif-style, gold accents, dark navy text
            const navy = [26, 39, 68];
            const gold = [201, 168, 76];
            const gray = [85, 85, 85];

            // Name
            doc.setFontSize(22);
            doc.setFont('times', 'normal');
            doc.setTextColor(...navy);
            doc.text(formData.name || 'Your Name', 20, y);
            y += 3;

            // Gold divider
            doc.setDrawColor(...gold);
            doc.setLineWidth(0.8);
            doc.line(20, y, 190, y);
            y += 7;

            // Contact info line
            doc.setFontSize(8);
            doc.setTextColor(...gray);
            doc.setFont('times', 'normal');
            const execContact = [formData.email, formData.phone, formData.address].filter(Boolean);
            if (execContact.length > 0) {
                doc.text(execContact.join('   |   '), 20, y);
                y += 5;
            }

            // Social links
            const execSocial = [formData.socialLinks?.linkedin, formData.socialLinks?.github, formData.socialLinks?.website].filter(Boolean);
            if (execSocial.length > 0) {
                doc.text(execSocial.join('   |   '), 20, y);
                y += 5;
            }

            // Photo (right-aligned, small)
            if (formData.photo) {
                try {
                    const imgProps = doc.getImageProperties(formData.photo);
                    const imgW = 25;
                    const imgH = (imgProps.height * imgW) / imgProps.width;
                    doc.addImage(formData.photo, 'JPEG', 170, 10, imgW, imgH);
                } catch (e) { /* skip */ }
            }

            y += 4;

            const execSection = (title) => {
                if (y > 265) { doc.addPage(); y = 15; }
                doc.setFontSize(9);
                doc.setFont('times', 'bold');
                doc.setTextColor(...navy);
                doc.text(title.toUpperCase(), 20, y);
                y += 1;
                doc.setDrawColor(...gold);
                doc.setLineWidth(0.5);
                doc.line(20, y, 80, y);
                y += 6;
            };

            // Professional Summary
            if (formData.summary) {
                execSection('Professional Summary');
                doc.setFontSize(9);
                doc.setFont('times', 'normal');
                doc.setTextColor(...gray);
                const summaryLines = doc.splitTextToSize(formData.summary, 170);
                doc.text(summaryLines, 20, y);
                y += summaryLines.length * 4.5 + 6;
            }

            // Experience
            execSection('Experience');
            doc.setFontSize(9);
            formData.experience.forEach((exp) => {
                if (exp.company) {
                    if (y > 265) { doc.addPage(); y = 15; }
                    doc.setFont('times', 'bold');
                    doc.setTextColor(...navy);
                    doc.text(exp.company, 20, y);
                    doc.setFont('times', 'italic');
                    doc.setTextColor(136, 136, 136);
                    doc.text(`${exp.startYear} - ${exp.endYear}`, 190, y, { align: 'right' });
                    y += 4.5;
                    doc.setFont('times', 'normal');
                    doc.setTextColor(...gray);
                    doc.text(exp.role, 20, y);
                    y += 7;
                }
            });

            // Education
            execSection('Education');
            doc.setFontSize(9);
            formData.education.forEach((edu) => {
                if (edu.institution) {
                    if (y > 265) { doc.addPage(); y = 15; }
                    doc.setFont('times', 'bold');
                    doc.setTextColor(...navy);
                    doc.text(edu.institution, 20, y);
                    doc.setFont('times', 'italic');
                    doc.setTextColor(136, 136, 136);
                    doc.text(`${edu.startYear} - ${edu.endYear}`, 190, y, { align: 'right' });
                    y += 4.5;
                    doc.setFont('times', 'normal');
                    doc.setTextColor(...gray);
                    doc.text(edu.degree, 20, y);
                    y += 7;
                }
            });

            // Skills
            if (formData.skills) {
                execSection('Skills');
                doc.setFontSize(9);
                doc.setFont('times', 'normal');
                doc.setTextColor(...gray);
                const skillLines = doc.splitTextToSize(formData.skills, 170);
                doc.text(skillLines, 20, y);
                y += skillLines.length * 4.5 + 4;
            }

            // Certifications
            if (formData.certifications?.some(c => c.name)) {
                execSection('Certifications');
                doc.setFontSize(9);
                formData.certifications.forEach((cert) => {
                    if (cert.name) {
                        if (y > 265) { doc.addPage(); y = 15; }
                        doc.setFont('times', 'bold');
                        doc.setTextColor(...navy);
                        doc.text(cert.name, 20, y);
                        doc.setTextColor(136, 136, 136);
                        doc.setFont('times', 'italic');
                        if (cert.year) doc.text(cert.year, 190, y, { align: 'right' });
                        y += 4.5;
                        doc.setFont('times', 'normal');
                        doc.setTextColor(...gray);
                        doc.text(cert.issuer, 20, y);
                        y += 7;
                    }
                });
            }

            // Projects
            if (formData.projects?.some(p => p.title)) {
                execSection('Projects');
                doc.setFontSize(9);
                formData.projects.forEach((proj) => {
                    if (proj.title) {
                        if (y > 265) { doc.addPage(); y = 15; }
                        doc.setFont('times', 'bold');
                        doc.setTextColor(...navy);
                        doc.text(proj.title, 20, y);
                        y += 4.5;
                        if (proj.technologies) {
                            doc.setFont('times', 'italic');
                            doc.setTextColor(...gold);
                            doc.text(proj.technologies, 20, y);
                            y += 4.5;
                        }
                        if (proj.description) {
                            doc.setFont('times', 'normal');
                            doc.setTextColor(...gray);
                            const descLines = doc.splitTextToSize(proj.description, 170);
                            doc.text(descLines, 20, y);
                            y += descLines.length * 4.5 + 2;
                        }
                        if (proj.link) {
                            doc.setTextColor(136, 136, 136);
                            doc.text(proj.link, 20, y);
                            y += 4.5;
                        }
                        y += 3;
                    }
                });
            }

            // Languages
            if (formData.languages) {
                execSection('Languages');
                doc.setFontSize(9);
                doc.setFont('times', 'normal');
                doc.setTextColor(...gray);
                doc.text(formData.languages, 20, y);
            }

        } else if (template === 'professional') {
            // Professional: blue header strip + two-column body
            const blue = [49, 130, 206];
            const darkText = [45, 55, 72];

            // Header strip background
            doc.setFillColor(...blue);
            doc.rect(0, 0, 210, 40, 'F');

            // Photo in header
            let headerTextX = 20;
            if (formData.photo) {
                try {
                    const imgProps = doc.getImageProperties(formData.photo);
                    const imgW = 22;
                    const imgH = (imgProps.height * imgW) / imgProps.width;
                    doc.addImage(formData.photo, 'JPEG', 15, 8, imgW, Math.min(imgH, 24));
                    headerTextX = 42;
                } catch (e) { /* skip */ }
            }

            // Name in header
            doc.setTextColor(255, 255, 255);
            doc.setFontSize(18);
            doc.setFont('helvetica', 'bold');
            doc.text(formData.name || 'Your Name', headerTextX, 18);

            // Summary in header
            if (formData.summary) {
                doc.setFontSize(8);
                doc.setFont('helvetica', 'normal');
                doc.setTextColor(255, 255, 255, 200);
                const summaryLines = doc.splitTextToSize(formData.summary, 155);
                doc.text(summaryLines.slice(0, 3), headerTextX, 25);
            }

            // Two-column body
            const sidebarX = 10;
            const sidebarW = 55;
            const mainX = 72;
            const mainW = 128;
            let sy = 48;
            let my = 48;

            // Sidebar background
            doc.setFillColor(240, 245, 250);
            doc.rect(0, 40, 65, 257, 'F');

            const proSideSection = (title) => {
                if (sy > 275) { doc.addPage(); sy = 15; doc.setFillColor(240, 245, 250); doc.rect(0, 0, 65, 297, 'F'); }
                doc.setFontSize(8);
                doc.setFont('helvetica', 'bold');
                doc.setTextColor(...darkText);
                doc.text(title.toUpperCase(), sidebarX, sy);
                sy += 1;
                doc.setDrawColor(...blue);
                doc.setLineWidth(0.4);
                doc.line(sidebarX, sy, sidebarX + 20, sy);
                sy += 5;
            };

            const proMainSection = (title) => {
                if (my > 275) { doc.addPage(); my = 15; doc.setFillColor(240, 245, 250); doc.rect(0, 0, 65, 297, 'F'); }
                doc.setFontSize(9);
                doc.setFont('helvetica', 'bold');
                doc.setTextColor(...darkText);
                doc.text(title.toUpperCase(), mainX, my);
                my += 1;
                doc.setDrawColor(...blue);
                doc.setLineWidth(0.4);
                doc.line(mainX, my, mainX + 25, my);
                my += 6;
            };

            // Sidebar: Contact
            proSideSection('Contact');
            doc.setFontSize(7);
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(80, 80, 80);
            if (formData.email) { doc.text(formData.email, sidebarX, sy); sy += 4; }
            if (formData.phone) { doc.text(formData.phone, sidebarX, sy); sy += 4; }
            if (formData.address) {
                const addrLines = doc.splitTextToSize(formData.address, sidebarW);
                doc.text(addrLines, sidebarX, sy);
                sy += addrLines.length * 3.5 + 2;
            }
            sy += 4;

            // Sidebar: Skills
            if (formData.skills) {
                proSideSection('Skills');
                doc.setFontSize(7);
                doc.setFont('helvetica', 'normal');
                doc.setTextColor(80, 80, 80);
                const skillLines = doc.splitTextToSize(formData.skills, sidebarW);
                doc.text(skillLines, sidebarX, sy);
                sy += skillLines.length * 3.5 + 4;
            }

            // Sidebar: Languages
            if (formData.languages) {
                proSideSection('Languages');
                doc.setFontSize(7);
                doc.setFont('helvetica', 'normal');
                doc.setTextColor(80, 80, 80);
                formData.languages.split(',').forEach((lang) => {
                    doc.text(lang.trim(), sidebarX, sy);
                    sy += 4;
                });
                sy += 2;
            }

            // Sidebar: Links
            const proSocial = [formData.socialLinks?.linkedin, formData.socialLinks?.github, formData.socialLinks?.website].filter(Boolean);
            if (proSocial.length > 0) {
                proSideSection('Links');
                doc.setFontSize(6);
                doc.setFont('helvetica', 'normal');
                doc.setTextColor(80, 80, 80);
                proSocial.forEach((link) => {
                    const linkLines = doc.splitTextToSize(link, sidebarW);
                    doc.text(linkLines, sidebarX, sy);
                    sy += linkLines.length * 3 + 3;
                });
            }

            // Main: Experience
            proMainSection('Experience');
            doc.setFontSize(9);
            formData.experience.forEach((exp) => {
                if (exp.company) {
                    if (my > 275) { doc.addPage(); my = 15; doc.setFillColor(240, 245, 250); doc.rect(0, 0, 65, 297, 'F'); }
                    doc.setFont('helvetica', 'bold');
                    doc.setTextColor(...darkText);
                    doc.text(exp.company, mainX, my);
                    doc.setFont('helvetica', 'normal');
                    doc.setTextColor(120, 120, 120);
                    doc.setFontSize(8);
                    doc.text(`${exp.startYear} - ${exp.endYear}`, mainX + mainW, my, { align: 'right' });
                    my += 4.5;
                    doc.setTextColor(100, 100, 100);
                    doc.text(exp.role, mainX, my);
                    my += 7;
                    doc.setFontSize(9);
                }
            });

            // Main: Education
            proMainSection('Education');
            doc.setFontSize(9);
            formData.education.forEach((edu) => {
                if (edu.institution) {
                    if (my > 275) { doc.addPage(); my = 15; doc.setFillColor(240, 245, 250); doc.rect(0, 0, 65, 297, 'F'); }
                    doc.setFont('helvetica', 'bold');
                    doc.setTextColor(...darkText);
                    doc.text(edu.institution, mainX, my);
                    doc.setFont('helvetica', 'normal');
                    doc.setTextColor(120, 120, 120);
                    doc.setFontSize(8);
                    doc.text(`${edu.startYear} - ${edu.endYear}`, mainX + mainW, my, { align: 'right' });
                    my += 4.5;
                    doc.setTextColor(100, 100, 100);
                    doc.text(edu.degree, mainX, my);
                    my += 7;
                    doc.setFontSize(9);
                }
            });

            // Main: Certifications
            if (formData.certifications?.some(c => c.name)) {
                proMainSection('Certifications');
                doc.setFontSize(9);
                formData.certifications.forEach((cert) => {
                    if (cert.name) {
                        if (my > 275) { doc.addPage(); my = 15; doc.setFillColor(240, 245, 250); doc.rect(0, 0, 65, 297, 'F'); }
                        doc.setFont('helvetica', 'bold');
                        doc.setTextColor(...darkText);
                        doc.text(cert.name, mainX, my);
                        doc.setFont('helvetica', 'normal');
                        doc.setTextColor(120, 120, 120);
                        doc.setFontSize(8);
                        if (cert.year) doc.text(cert.year, mainX + mainW, my, { align: 'right' });
                        my += 4.5;
                        doc.setTextColor(100, 100, 100);
                        doc.text(cert.issuer, mainX, my);
                        my += 7;
                        doc.setFontSize(9);
                    }
                });
            }

            // Main: Projects
            if (formData.projects?.some(p => p.title)) {
                proMainSection('Projects');
                doc.setFontSize(9);
                formData.projects.forEach((proj) => {
                    if (proj.title) {
                        if (my > 275) { doc.addPage(); my = 15; doc.setFillColor(240, 245, 250); doc.rect(0, 0, 65, 297, 'F'); }
                        doc.setFont('helvetica', 'bold');
                        doc.setTextColor(...darkText);
                        doc.text(proj.title, mainX, my);
                        my += 4.5;
                        if (proj.technologies) {
                            doc.setFont('helvetica', 'italic');
                            doc.setTextColor(...blue);
                            doc.setFontSize(8);
                            doc.text(proj.technologies, mainX, my);
                            my += 4;
                        }
                        if (proj.description) {
                            doc.setFont('helvetica', 'normal');
                            doc.setTextColor(100, 100, 100);
                            doc.setFontSize(8);
                            const descLines = doc.splitTextToSize(proj.description, mainW);
                            doc.text(descLines, mainX, my);
                            my += descLines.length * 4 + 2;
                        }
                        if (proj.link) {
                            doc.setTextColor(120, 120, 120);
                            doc.setFontSize(7);
                            doc.text(proj.link, mainX, my);
                            my += 4;
                        }
                        my += 3;
                        doc.setFontSize(9);
                    }
                });
            }

        } else if (template === 'creative') {
            // Creative: main content left, gradient sidebar right
            const purple1 = [102, 126, 234];
            const purple2 = [118, 75, 162];

            // Right sidebar background (gradient simulated with two rects)
            doc.setFillColor(...purple1);
            doc.rect(140, 0, 70, 148, 'F');
            doc.setFillColor(...purple2);
            doc.rect(140, 148, 70, 149, 'F');

            // Sidebar content (white text)
            let sy = 20;
            doc.setTextColor(255, 255, 255);

            // Photo in sidebar
            if (formData.photo) {
                try {
                    const imgProps = doc.getImageProperties(formData.photo);
                    const imgW = 35;
                    const imgH = (imgProps.height * imgW) / imgProps.width;
                    doc.addImage(formData.photo, 'JPEG', 157, sy - 5, imgW, Math.min(imgH, 35));
                    sy += Math.min(imgH, 35) + 5;
                } catch (e) { /* skip */ }
            }

            // Sidebar: Contact
            doc.setFontSize(7);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(255, 255, 255, 180);
            doc.text('CONTACT', 148, sy);
            sy += 5;
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(255, 255, 255);
            doc.setFontSize(7);
            if (formData.email) { doc.text(formData.email, 148, sy); sy += 4; }
            if (formData.phone) { doc.text(formData.phone, 148, sy); sy += 4; }
            if (formData.address) {
                const addrLines = doc.splitTextToSize(formData.address, 55);
                doc.text(addrLines, 148, sy);
                sy += addrLines.length * 3.5 + 2;
            }
            sy += 5;

            // Sidebar: Skills
            if (formData.skills) {
                doc.setFontSize(7);
                doc.setFont('helvetica', 'bold');
                doc.setTextColor(255, 255, 255, 180);
                doc.text('SKILLS', 148, sy);
                sy += 5;
                doc.setFont('helvetica', 'normal');
                doc.setTextColor(255, 255, 255);
                doc.setFontSize(7);
                const skillLines = doc.splitTextToSize(formData.skills, 55);
                doc.text(skillLines, 148, sy);
                sy += skillLines.length * 3.5 + 5;
            }

            // Sidebar: Languages
            if (formData.languages) {
                doc.setFontSize(7);
                doc.setFont('helvetica', 'bold');
                doc.setTextColor(255, 255, 255, 180);
                doc.text('LANGUAGES', 148, sy);
                sy += 5;
                doc.setFont('helvetica', 'normal');
                doc.setTextColor(255, 255, 255);
                doc.setFontSize(7);
                formData.languages.split(',').forEach((lang) => {
                    doc.text(lang.trim(), 148, sy);
                    sy += 4;
                });
                sy += 3;
            }

            // Sidebar: Links
            const creativeSocial = [formData.socialLinks?.linkedin, formData.socialLinks?.github, formData.socialLinks?.website].filter(Boolean);
            if (creativeSocial.length > 0) {
                doc.setFontSize(7);
                doc.setFont('helvetica', 'bold');
                doc.setTextColor(255, 255, 255, 180);
                doc.text('LINKS', 148, sy);
                sy += 5;
                doc.setFont('helvetica', 'normal');
                doc.setTextColor(255, 255, 255);
                doc.setFontSize(6);
                creativeSocial.forEach((link) => {
                    const linkLines = doc.splitTextToSize(link, 55);
                    doc.text(linkLines, 148, sy);
                    sy += linkLines.length * 3 + 3;
                });
            }

            // Main content (left side)
            doc.setTextColor(45, 45, 45);
            let my = 20;

            // Name
            doc.setFontSize(20);
            doc.setFont('helvetica', 'bold');
            doc.text(formData.name || 'Your Name', 15, my);
            my += 8;

            // Summary
            if (formData.summary) {
                doc.setFontSize(9);
                doc.setFont('helvetica', 'normal');
                doc.setTextColor(102, 102, 102);
                const summaryLines = doc.splitTextToSize(formData.summary, 115);
                doc.text(summaryLines, 15, my);
                my += summaryLines.length * 4.5 + 6;
            }

            const creativeSection = (title) => {
                if (my > 270) { doc.addPage(); my = 15; doc.setFillColor(...purple1); doc.rect(140, 0, 70, 148, 'F'); doc.setFillColor(...purple2); doc.rect(140, 148, 70, 149, 'F'); }
                doc.setFontSize(8);
                doc.setFont('helvetica', 'bold');
                doc.setTextColor(102, 126, 234);
                doc.text(title.toUpperCase(), 15, my);
                my += 5;
                doc.setTextColor(45, 45, 45);
            };

            // Experience (timeline style)
            creativeSection('Experience');
            doc.setFontSize(9);
            formData.experience.forEach((exp) => {
                if (exp.company) {
                    if (my > 270) { doc.addPage(); my = 15; doc.setFillColor(...purple1); doc.rect(140, 0, 70, 148, 'F'); doc.setFillColor(...purple2); doc.rect(140, 148, 70, 149, 'F'); }
                    // Timeline dot
                    doc.setFillColor(...purple1);
                    doc.circle(17, my - 1, 1.2, 'F');
                    // Timeline line
                    doc.setDrawColor(224, 224, 224);
                    doc.setLineWidth(0.3);
                    doc.line(17, my + 1, 17, my + 12);

                    doc.setFont('helvetica', 'bold');
                    doc.setTextColor(45, 45, 45);
                    doc.text(exp.company, 22, my);
                    my += 4;
                    doc.setFont('helvetica', 'normal');
                    doc.setTextColor(118, 75, 162);
                    doc.setFontSize(8);
                    doc.text(exp.role, 22, my);
                    my += 3.5;
                    doc.setTextColor(120, 120, 120);
                    doc.setFontSize(7);
                    doc.text(`${exp.startYear} - ${exp.endYear}`, 22, my);
                    my += 7;
                    doc.setFontSize(9);
                }
            });

            // Education (timeline style)
            creativeSection('Education');
            doc.setFontSize(9);
            formData.education.forEach((edu) => {
                if (edu.institution) {
                    if (my > 270) { doc.addPage(); my = 15; doc.setFillColor(...purple1); doc.rect(140, 0, 70, 148, 'F'); doc.setFillColor(...purple2); doc.rect(140, 148, 70, 149, 'F'); }
                    doc.setFillColor(...purple1);
                    doc.circle(17, my - 1, 1.2, 'F');
                    doc.setDrawColor(224, 224, 224);
                    doc.setLineWidth(0.3);
                    doc.line(17, my + 1, 17, my + 12);

                    doc.setFont('helvetica', 'bold');
                    doc.setTextColor(45, 45, 45);
                    doc.text(edu.institution, 22, my);
                    my += 4;
                    doc.setFont('helvetica', 'normal');
                    doc.setTextColor(118, 75, 162);
                    doc.setFontSize(8);
                    doc.text(edu.degree, 22, my);
                    my += 3.5;
                    doc.setTextColor(120, 120, 120);
                    doc.setFontSize(7);
                    doc.text(`${edu.startYear} - ${edu.endYear}`, 22, my);
                    my += 7;
                    doc.setFontSize(9);
                }
            });

            // Certifications (timeline style)
            if (formData.certifications?.some(c => c.name)) {
                creativeSection('Certifications');
                doc.setFontSize(9);
                formData.certifications.forEach((cert) => {
                    if (cert.name) {
                        if (my > 270) { doc.addPage(); my = 15; doc.setFillColor(...purple1); doc.rect(140, 0, 70, 148, 'F'); doc.setFillColor(...purple2); doc.rect(140, 148, 70, 149, 'F'); }
                        doc.setFillColor(...purple1);
                        doc.circle(17, my - 1, 1.2, 'F');
                        doc.setDrawColor(224, 224, 224);
                        doc.setLineWidth(0.3);
                        doc.line(17, my + 1, 17, my + 8);

                        doc.setFont('helvetica', 'bold');
                        doc.setTextColor(45, 45, 45);
                        doc.text(cert.name, 22, my);
                        my += 4;
                        doc.setFont('helvetica', 'normal');
                        doc.setTextColor(120, 120, 120);
                        doc.setFontSize(8);
                        doc.text(`${cert.issuer}${cert.year ? ' | ' + cert.year : ''}`, 22, my);
                        my += 6;
                        doc.setFontSize(9);
                    }
                });
            }

            // Projects (timeline style)
            if (formData.projects?.some(p => p.title)) {
                creativeSection('Projects');
                doc.setFontSize(9);
                formData.projects.forEach((proj) => {
                    if (proj.title) {
                        if (my > 270) { doc.addPage(); my = 15; doc.setFillColor(...purple1); doc.rect(140, 0, 70, 148, 'F'); doc.setFillColor(...purple2); doc.rect(140, 148, 70, 149, 'F'); }
                        doc.setFillColor(...purple1);
                        doc.circle(17, my - 1, 1.2, 'F');
                        doc.setDrawColor(224, 224, 224);
                        doc.setLineWidth(0.3);
                        doc.line(17, my + 1, 17, my + 12);

                        doc.setFont('helvetica', 'bold');
                        doc.setTextColor(45, 45, 45);
                        doc.text(proj.title, 22, my);
                        my += 4;
                        if (proj.technologies) {
                            doc.setFont('helvetica', 'italic');
                            doc.setTextColor(102, 126, 234);
                            doc.setFontSize(7);
                            doc.text(proj.technologies, 22, my);
                            my += 3.5;
                        }
                        if (proj.description) {
                            doc.setFont('helvetica', 'normal');
                            doc.setTextColor(120, 120, 120);
                            doc.setFontSize(8);
                            const descLines = doc.splitTextToSize(proj.description, 110);
                            doc.text(descLines, 22, my);
                            my += descLines.length * 4 + 2;
                        }
                        my += 3;
                        doc.setFontSize(9);
                    }
                });
            }
        }

        doc.save('resume.pdf');
        setSnackbarMessage('PDF exported successfully!');
        setSnackbarOpen(true);
    };

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <div className={`App ${darkMode ? 'dark-mode' : 'light-mode'}`}>
                <Container maxWidth="xl" sx={{ py: 3 }}>
                    {/* Header */}
                    <Box className="app-header" display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                        <Box>
                            <Typography variant="h4">
                                Resume Builder
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: -0.5 }}>
                                Build, preview & export your professional resume
                            </Typography>
                        </Box>
                        <Box display="flex" gap={1} alignItems="center">
                            <input
                                type="file"
                                accept=".pdf"
                                ref={fileInputRef}
                                style={{ display: 'none' }}
                                onChange={importFromPDF}
                            />
                            <Tooltip title="Import resume from PDF">
                                <span>
                                    <IconButton
                                        onClick={() => fileInputRef.current?.click()}
                                        color="primary"
                                        size="small"
                                        sx={{ border: '1px solid' }}
                                        disabled={importing}
                                    >
                                        {importing ? <CircularProgress size={20} /> : <UploadFileIcon />}
                                    </IconButton>
                                </span>
                            </Tooltip>
                            <Tooltip title="Clear form">
                                <IconButton onClick={clearForm} className="clear-btn" size="small" sx={{ border: '1px solid' }}>
                                    <RestartAltIcon />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title={darkMode ? 'Light mode' : 'Dark mode'}>
                                <IconButton onClick={() => setDarkMode(!darkMode)} className="theme-toggle" color="primary">
                                    {darkMode ? <LightModeIcon /> : <DarkModeIcon />}
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </Box>

                    {/* Main Content */}
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={5}>
                            <Paper className="glass-card" elevation={0} sx={{ p: 2.5 }}>
                                <Form formData={formData} setFormData={setFormData} />
                            </Paper>
                        </Grid>
                        <Grid item xs={12} md={7}>
                            <Paper className="glass-card" elevation={0} sx={{ p: 2.5 }}>
                                <Preview formData={formData} template={template} setTemplate={setTemplate} />
                            </Paper>
                            <Box display="flex" justifyContent="center" mt={2} gap={1} alignItems="center">
                                <Button
                                    className="gradient-btn"
                                    variant="contained"
                                    startIcon={<SaveAltIcon />}
                                    onClick={saveToPDF}
                                    size="large"
                                >
                                    Export {template.charAt(0).toUpperCase() + template.slice(1)} to PDF
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                </Container>

                <Snackbar
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                    open={snackbarOpen}
                    autoHideDuration={4000}
                    onClose={handleSnackbarClose}
                    TransitionComponent={Slide}
                    message={snackbarMessage}
                    action={
                        <IconButton size="small" color="inherit" onClick={handleSnackbarClose}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    }
                />
            </div>
        </ThemeProvider>
    );
}

export default App;
